import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type {
  AgendaItem,
  AgendaItemSignalState,
  AgendaItemStatus,
} from "@elation/api-client/src/stitch-web";
import {
  type CreateAgendaItemMutationError,
  type DeleteAgendaItemMutationError,
  getListAgendaItemsQueryKey,
  type UpdateAgendaItemMutationError,
  useCreateAgendaItem,
  useDeleteAgendaItem,
  useUpdateAgendaItem,
} from "@elation/api-client/src/stitch-web";

import type { CachedAgendaItemList } from "@src/features/agenda/domain";
import { usePracticeId } from "@src/features/practices";

import { isPendingAgendaItemId } from "./reconcile-ordered-agenda-items";

// Monotonic counter for optimistic placeholder IDs. Module-level so multiple
// Agenda mounts in the same session don't collide. Real server IDs are
// positive; placeholders are negative.
let tempIdCounter = 0;

export type PendingItemId = number & { readonly __brand: "pending-item-id" };

const mintPendingId = (): PendingItemId => -++tempIdCounter as PendingItemId;

export const isPendingAgendaItem = (id: number): id is PendingItemId =>
  isPendingAgendaItemId(id);

function getOptimisticAgendaItemPatch(data: {
  status?: AgendaItemStatus;
  description?: string;
  healthConcernId?: number | null;
}): Partial<AgendaItem> {
  // A concern-id patch makes the nested `healthConcern` stale — grouping
  // (`concernGroupKey`) prefers the nested field over the flat id, so
  // leaving a stale `healthConcern` in place would keep the item grouped
  // under its OLD concern until the server round-trip re-enriches it.
  const concernPatch: Partial<AgendaItem> =
    data.healthConcernId !== undefined ? { healthConcern: null } : {};

  if (data.status !== "open") return { ...data, ...concernPatch };

  return {
    ...data,
    ...concernPatch,
    signalState: "open",
    completedAt: null,
    completedBy: undefined,
    discussedAt: null,
    assessedAt: null,
    plannedAt: null,
    followUpAt: null,
  };
}

export type AgendaItemMutationVerb = "create" | "update" | "delete";

export interface AgendaItemMutationError {
  verb: AgendaItemMutationVerb;
  // HTTP status; null for network failures or cancellations (no response).
  status: number | null;
}

export interface UseAgendaItemMutationsParams {
  encounterId: number | undefined;
  patientId: number | undefined;
}

export interface UseAgendaItemMutationsResult {
  createAgendaItem: (description: string) => Promise<void>;
  toggleAgendaItem: (
    itemId: number,
    currentStatus: AgendaItemStatus,
    currentSignalState: AgendaItemSignalState,
  ) => Promise<void>;
  /**
   * v2-only: the deprecated Today page agenda has no inline rename affordance.
   */
  updateAgendaItemDescription: (
    itemId: number,
    description: string,
  ) => Promise<void>;
  /**
   * v2-only: the deprecated Today page agenda has no cross-concern drag.
   * Persists a custom item's cross-concern-group move. Must be awaited
   * before scheduling a position-only reorder — the reorder mutation always
   * invalidates the agenda query on settle, and a refetch that lands before
   * this PATCH completes would revert the item's concern while keeping its
   * new position (see the `onItemsChange` doc on AgendaGroupedSortableList).
   */
  updateAgendaItemConcern: (
    itemId: number,
    healthConcernId: number | null,
  ) => Promise<void>;
  deleteAgendaItem: (itemId: number) => Promise<void>;
  error: AgendaItemMutationError | null;
  /**
   * v2-only: reconcileOrderedAgendaItems uses these to keep optimistic
   * create placeholders stable across SSE/refetch reconciliation.
   */
  pendingCreateIds: PendingItemId[];
  completedCreateIds: Map<PendingItemId, number>;
}

export function useAgendaItemMutations({
  encounterId,
  patientId,
}: UseAgendaItemMutationsParams): UseAgendaItemMutationsResult {
  const practiceId = usePracticeId();
  const queryClient = useQueryClient();
  // Tracked manually so any successful mutation clears the banner, even if
  // it's a different verb from the one that failed. React Query's per-hook
  // `isError` flag only resets when the *same* mutation is re-invoked.
  const [error, setError] = useState<AgendaItemMutationError | null>(null);
  const [pendingCreateIds, setPendingCreateIds] = useState<PendingItemId[]>([]);
  const [completedCreateIds, setCompletedCreateIds] = useState<
    Map<PendingItemId, number>
  >(() => new Map());

  type CreateMutationVars = {
    encounterId: number;
    data: { description: string };
  };

  type CreateContext =
    | { kind: "noop" }
    | {
        kind: "optimistic";
        previous: CachedAgendaItemList | undefined;
        tempId: PendingItemId;
      };

  const { mutateAsync: createMutation } = useCreateAgendaItem<
    CreateAgendaItemMutationError,
    CreateContext
  >({
    mutation: {
      onMutate: async (vars: CreateMutationVars): Promise<CreateContext> => {
        if (patientId == null) {
          return { kind: "noop" };
        }

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        const tempId = mintPendingId();
        setPendingCreateIds((ids) => [...ids, tempId]);
        const now = new Date().toISOString();
        const placeholder: AgendaItem = {
          id: tempId,
          description: vars.data.description,
          type: "custom",
          status: "open",
          signalState: "open",
          patientId,
          practiceId,
          encounterId: vars.encounterId,
          sortOrder: 0,
          healthConcern: null,
          medicalCodes: [],
          citations: [],
          createdAt: now,
          updatedAt: now,
        };

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...old,
          data: {
            ...old?.data,
            items: [...(old?.data?.items ?? []), placeholder],
          },
        }));

        return { kind: "optimistic", previous, tempId };
      },
      onSuccess: (envelope, vars, context) => {
        if (!context || context.kind === "noop") return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        const serverItem = envelope.data;
        const { tempId } = context;

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => {
          const items = old?.data?.items ?? [];
          // Three orderings to reconcile:
          //   1. Real item is already in the cache (SSE-first race): drop
          //      the placeholder so the list ends up with exactly one copy.
          //   2. Placeholder is still in the cache (common case): swap it
          //      in-place for the server item.
          //   3. Neither placeholder nor real item is in the cache — e.g.
          //      an SSE snapshot after a reconnect replaced `items` before
          //      this callback ran. Append the server item so it is not
          //      silently dropped; the eventual `agenda_item.created` SSE
          //      event will dedup by id.
          const realAlreadyPresent = items.some((i) => i.id === serverItem.id);

          if (realAlreadyPresent) {
            return {
              ...old,
              data: {
                ...old?.data,
                items: items.filter((i) => i.id !== tempId),
              },
            };
          }

          const placeholderPresent = items.some((i) => i.id === tempId);
          const next = placeholderPresent
            ? items.map((i) => (i.id === tempId ? serverItem : i))
            : [...items, serverItem];

          return {
            ...old,
            data: { ...old?.data, items: next },
          };
        });
        setError(null);
        setPendingCreateIds((ids) => ids.filter((id) => id !== tempId));
        setCompletedCreateIds((ids) => {
          const next = new Map(ids);
          next.set(tempId, serverItem.id);
          return next;
        });
      },
      onError: (err, vars, context) => {
        console.error("[useAgendaItemMutations] create failed", {
          encounterId: vars.encounterId,
          tempId: context?.kind === "optimistic" ? context.tempId : undefined,
          description: vars.data.description,
          error: err,
        });
        setError({ verb: "create", status: err.response?.status ?? null });
        if (!context || context.kind === "noop") return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        queryClient.setQueryData(queryKey, context.previous);
        setPendingCreateIds((ids) => ids.filter((id) => id !== context.tempId));
        setCompletedCreateIds((ids) => {
          if (!ids.has(context.tempId)) return ids;
          const next = new Map(ids);
          next.delete(context.tempId);
          return next;
        });
      },
      // No onSettled: deliberately skip invalidation to avoid a refetch that
      // races against SSE and re-introduces flicker. The SSE handler keeps
      // the cache current; the 5s polling refetchInterval (fallback mode)
      // catches any drift.
    },
  });

  type UpdateMutationVars = {
    encounterId: number;
    itemId: number;
    data: {
      status?: AgendaItemStatus;
      description?: string;
      healthConcernId?: number | null;
    };
  };

  type UpdateContext = {
    previous: CachedAgendaItemList | undefined;
  };

  const { mutateAsync: updateMutation } = useUpdateAgendaItem<
    UpdateAgendaItemMutationError,
    UpdateContext
  >({
    mutation: {
      onMutate: async (vars: UpdateMutationVars): Promise<UpdateContext> => {
        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...old,
          data: {
            ...old?.data,
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.itemId
                ? { ...i, ...getOptimisticAgendaItemPatch(vars.data) }
                : i,
            ),
          },
        }));

        return { previous };
      },
      onSuccess: (envelope, vars) => {
        setError(null);

        const serverItem = envelope.data;
        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...old,
          data: {
            ...old?.data,
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.itemId ? serverItem : i,
            ),
          },
        }));
      },
      onError: (err, vars, context) => {
        console.error("[useAgendaItemMutations] update failed", {
          encounterId: vars.encounterId,
          itemId: vars.itemId,
          status: vars.data.status,
          description: vars.data.description,
          healthConcernId: vars.data.healthConcernId,
          error: err,
        });
        setError({ verb: "update", status: err.response?.status ?? null });
        if (!context) return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  type DeleteMutationVars = {
    encounterId: number;
    itemId: number;
  };

  type DeleteContext = {
    previous: CachedAgendaItemList | undefined;
  };

  const { mutateAsync: deleteMutation } = useDeleteAgendaItem<
    DeleteAgendaItemMutationError,
    DeleteContext
  >({
    mutation: {
      onMutate: async (vars: DeleteMutationVars): Promise<DeleteContext> => {
        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...old,
          data: {
            ...old?.data,
            items: (old?.data?.items ?? []).filter((i) => i.id !== vars.itemId),
          },
        }));

        return { previous };
      },
      onSuccess: (_data, vars) => {
        setError(null);
        setCompletedCreateIds((ids) => {
          const entries = [...ids.entries()].filter(
            ([, serverId]) => serverId !== vars.itemId,
          );

          return entries.length === ids.size ? ids : new Map(entries);
        });
      },
      onError: (err, vars, context) => {
        console.error("[useAgendaItemMutations] delete failed", {
          encounterId: vars.encounterId,
          itemId: vars.itemId,
          error: err,
        });
        setError({ verb: "delete", status: err.response?.status ?? null });
        if (!context) return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  const createAgendaItem = async (description: string) => {
    if (encounterId == null || patientId == null) return;
    await createMutation({ encounterId, data: { description } });
  };

  const toggleAgendaItem = async (
    itemId: number,
    currentStatus: AgendaItemStatus,
    currentSignalState: AgendaItemSignalState,
  ) => {
    if (encounterId == null) return;

    // An item reads as "complete" to the user if either the clinician
    // finalized it manually OR the AI signal accumulator landed on planned.
    // Reopening clears all four signal timestamps server-side.
    const isCompleted =
      currentStatus === "finalized" || currentSignalState === "planned";
    const nextStatus: AgendaItemStatus = isCompleted ? "open" : "finalized";

    // Don't no-op when nextStatus === currentStatus AND the row is open with
    // AI signals: that's the "Mark incomplete on AI-completed item" path,
    // which needs a PATCH so the server can route through reopen() and
    // clear the four signal timestamps. The server's PATCH route now also
    // detects this "open + signals → clear" case.
    if (
      nextStatus === currentStatus &&
      !(nextStatus === "open" && currentSignalState !== "open")
    ) {
      return;
    }

    await updateMutation({ encounterId, itemId, data: { status: nextStatus } });
  };

  const updateAgendaItemDescription = async (
    itemId: number,
    description: string,
  ) => {
    // Negative ids are optimistic create placeholders with no server row
    // yet — there is nothing to rename until the create resolves.
    if (encounterId == null || isPendingAgendaItem(itemId)) return;
    await updateMutation({ encounterId, itemId, data: { description } });
  };

  const updateAgendaItemConcern = async (
    itemId: number,
    healthConcernId: number | null,
  ) => {
    // Negative ids are optimistic create placeholders with no server row
    // yet — there is nothing to re-concern until the create resolves.
    if (encounterId == null || isPendingAgendaItem(itemId)) return;
    await updateMutation({ encounterId, itemId, data: { healthConcernId } });
  };

  const deleteAgendaItem = async (itemId: number) => {
    if (encounterId == null) return;
    await deleteMutation({ encounterId, itemId });
  };

  return {
    createAgendaItem,
    toggleAgendaItem,
    updateAgendaItemDescription,
    updateAgendaItemConcern,
    deleteAgendaItem,
    error,
    pendingCreateIds,
    completedCreateIds,
  };
}
